<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('projectProgressionReport.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          { text: $t('projectProgressionReport.pageTitle'), active: true }
        ]"
      />
    </div>

    <!-- Lock whole banner when loading or syncing -->
    <ts-loading-banner :loading="loading || syncing">
      <ts-card>
        <ts-card-wrapper>
          <div class="row">
            <div class="col-md-4">
              <label>{{ $t('projectProgressionReport.project') }}</label>
              <a-select
                v-model="model.project_id"
                :options="projects"
                showSearch
                :filterOption="filterOption"
                class="ts-w-full"
                allowClear
                :disabled="loading || syncing"
                @change="fetchProjectRelatedData"
              />
              <div class="validate" v-if="errors.has('project_id')">
                {{ errors.first('project_id') }}
              </div>
            </div>
            <div class="col-md-4">
              <label>Template</label>
              <a-select
                v-model="templateStyle"
                class="ts-w-full"
                :disabled="loading || syncing || loadingExport"
              >
                <a-select-option value="standard">Standard</a-select-option>
                <a-select-option value="summary"
                  >Quantity summary</a-select-option
                >
              </a-select>
            </div>
          </div>

          <!-- Left / Right task box -->
          <div class="row ts-mt-3">
            <!-- Left Column: All Tasks (5 cols) -->
            <div class="col-md-5">
              <div
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <label class="mb-0">{{
                  $t('projectProgressionReport.allTasks')
                }}</label>
                <!-- Select All Left -->
                <label
                  v-if="availableTasks.length > 0"
                  class="select-all-label"
                >
                  <input
                    type="checkbox"
                    v-model="selectAllAvailable"
                    :disabled="syncing"
                    class="task-checkbox"
                  />
                  <span>Select All</span>
                </label>
              </div>
              <div class="task-box">
                <div v-if="availableTasks.length === 0" class="task-box-empty">
                  {{ $t('projectProgressionReport.noTaskAvailable') }}
                </div>
                <label
                  v-for="task in availableTasks"
                  :key="task.id"
                  class="task-box-item"
                  :class="{ 'is-disabled': syncing }"
                >
                  <input
                    type="checkbox"
                    :value="task.id"
                    v-model="checkedTaskIds"
                    :disabled="syncing"
                    class="task-checkbox"
                  />
                  <span>{{ task.label }}</span>
                </label>
              </div>
            </div>

            <!-- Middle Column: Action Arrows (1 col) -->
            <div class="col-md-1 arrow-column">
              <button
                type="button"
                class="arrow-btn arrow-btn-right"
                :disabled="checkedTaskIds.length === 0 || syncing"
                @click="moveCheckedTasks"
                title="Move selected tasks to right"
              >
                →
              </button>
              <button
                type="button"
                class="arrow-btn arrow-btn-left"
                :disabled="checkedOrderedIds.length === 0 || syncing"
                @click="removeCheckedTasks"
                title="Move selected tasks back to left"
              >
                ←
              </button>
            </div>

            <!-- Right Column: Display Order (6 cols to fill layout seamlessly) -->
            <div class="col-md-6">
              <div
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <label class="mb-0">{{
                  $t('projectProgressionReport.displayOrder')
                }}</label>
                <!-- Select All Right -->
                <label v-if="orderedTasks.length > 0" class="select-all-label">
                  <input
                    type="checkbox"
                    v-model="selectAllOrdered"
                    :disabled="syncing"
                    class="task-checkbox"
                  />
                  <span>Select All</span>
                </label>
              </div>
              <div class="task-box">
                <div v-if="orderedTasks.length === 0" class="task-box-empty">
                  {{ $t('projectProgressionReport.noTaskSelected') }}
                </div>
                <div
                  v-for="(item, index) in orderedTasks"
                  :key="item.id"
                  class="task-box-item ordered-item"
                  :draggable="!syncing"
                  @dragstart="syncing ? null : onDragStart(index)"
                  @dragover.prevent
                  @dragenter.prevent="syncing ? null : onDragEnter(index)"
                  @dragend="syncing ? null : onDragEnd()"
                  :class="{
                    'is-dragging': dragIndex === index,
                    'is-disabled': syncing
                  }"
                >
                  <input
                    type="checkbox"
                    :value="item.id"
                    v-model="checkedOrderedIds"
                    :disabled="syncing"
                    class="task-checkbox"
                    @click.stop
                  />
                  <span class="drag-handle">⠿</span>
                  <span class="order-badge">{{ item.order }}</span>
                  <span class="task-label">{{ item.label }}</span>
                </div>
              </div>

              <!-- ACTION BUTTONS -->
              <div
                class="ts-mt-3 d-flex justify-content-end align-items-center"
              >
                <!-- View Button First -->
                <ts-button
                  color="primary"
                  size="sm"
                  class="ts-mr-2"
                  :disabled="!model.project_id"
                  @click="onPreview"
                >
                  {{ $t('preview') }}
                </ts-button>

                <!-- Excel Button Second -->
                <ts-button
                  color="info"
                  size="sm"
                  class="d-inline-flex align-items-center justify-content-center"
                  :loading="loadingExport"
                  :disabled="!model.project_id || loadingExport"
                  @click="onExportExcel"
                >
                  <i class="fa fa-file-excel-o mr-1"></i>
                  <span>{{ $t('excel') }}</span>
                </ts-button>
              </div>
            </div>
          </div>

          <div class="row ts-mt-2">
            <div class="col-md-12">
              <small class="ts-text-muted">{{ syncStatusLabel }}</small>
            </div>
          </div>
        </ts-card-wrapper>
      </ts-card>

      <!-- REPORT TABLE -->
      <ts-card class="ts-mt-3" v-if="resources.length > 0">
        <ts-card-wrapper>
          <div class="progression-ts-table-wrapper">
            <ts-table :columns="progressionColumns" :records="tableRecords">
              <template v-slot="{ record }">
                <template v-if="record.isFooter">
                  <td colspan="2" class="summary-cell text-right">
                    {{ record.label }}
                  </td>
                  <td class="summary-cell text-center">
                    {{ record.new_fiber }}
                  </td>
                  <td class="summary-cell text-center">
                    {{ record.refreshment }}
                  </td>
                  <td
                    v-for="col in taskColumns"
                    :key="col"
                    class="summary-cell text-center"
                  >
                    {{ record.tasks ? record.tasks[col] : '' }}
                  </td>
                </template>
                <template v-else>
                  <td
                    class="white-space-nowrap text-center align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{ record.isLegend ? '' : record.no }}
                  </td>
                  <td
                    class="white-space-nowrap align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{ record.isLegend ? 'TOTAL QTY' : record.site_name }}
                  </td>
                  <td
                    v-if="!isSummaryTemplate"
                    class="white-space-nowrap text-center align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{
                      record.isLegend ? totals.po_distance : record.po_distance
                    }}
                  </td>
                  <td
                    v-if="!isSummaryTemplate"
                    class="white-space-nowrap text-center align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{
                      record.isLegend
                        ? totals.actual_completion
                        : record.actual_completion
                    }}
                  </td>
                  <td
                    v-if="!isSummaryTemplate"
                    class="white-space-nowrap text-center align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{
                      record.isLegend
                        ? totals.progression_percent + '%'
                        : record.progression_percent + '%'
                    }}
                  </td>
                  <td
                    class="white-space-nowrap text-center align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{ record.isLegend ? totals.new_fiber : record.new_fiber }}
                  </td>
                  <td
                    class="white-space-nowrap text-center align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{
                      record.isLegend ? totals.refreshment : record.refreshment
                    }}
                  </td>
                  <td
                    v-for="col in taskColumns"
                    :key="col"
                    class="white-space-nowrap text-center align-middle"
                    :class="{ 'legend-cell': record.isLegend }"
                  >
                    {{
                      record.isLegend
                        ? totals.tasks[col]
                        : record.tasks
                        ? record.tasks[col]
                        : 0
                    }}
                  </td>
                </template>
              </template>
            </ts-table>
          </div>
        </ts-card-wrapper>
      </ts-card>
    </ts-loading-banner>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

const REPORT_OBJECT_NAME = 'Project Progression Report'

export default {
  name: 'projectProgressionReport',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      loadingExport: false,
      syncing: false,
      projects: [],
      allTasksRaw: [],
      orderedTasks: [],
      checkedTaskIds: [],
      checkedOrderedIds: [],
      dragIndex: null,
      resources: [],
      templateStyle: 'standard',
      taskColumns: [],
      model: {
        project_id: undefined
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  computed: {
    isSummaryTemplate() {
      return this.templateStyle === 'summary'
    },
    summaryFooter() {
      const estimated = this.resources.reduce(
        (sum, row) => {
          sum.new_fiber += Number(row.estimated_new_fiber) || 0
          sum.refreshment += Number(row.estimated_refreshment) || 0
          return sum
        },
        { new_fiber: 0, refreshment: 0 }
      )
      const percent = key =>
        estimated[key] > 0
          ? Math.round((this.totals[key] / estimated[key]) * 100) + '%'
          : '0%'
      return [
        {
          isFooter: true,
          label: 'SUM QTY',
          new_fiber: this.totals.new_fiber,
          refreshment: this.totals.refreshment,
          tasks: this.totals.tasks
        },
        {
          isFooter: true,
          label: 'SUM Estimated QTY',
          new_fiber: estimated.new_fiber,
          refreshment: estimated.refreshment
        },
        {
          isFooter: true,
          label: 'Progression %',
          new_fiber: percent('new_fiber'),
          refreshment: percent('refreshment')
        }
      ]
    },
    progressionColumns() {
      const fixed = [
        { name: this.$t('projectProgressionReport.numbering') || 'No' },
        { name: 'Site Name', style: 'width: 150px; min-width: 150px;' },
        { name: 'PO Distance', style: 'text-align:center' },
        { name: 'Actual Completion', style: 'text-align:center' },
        { name: 'Progression %', style: 'text-align:center' },
        { name: 'New Fiber', style: 'text-align:center' },
        { name: 'Refreshment', style: 'text-align:center' }
      ]
      const dynamic = this.taskColumns.map(col => ({ name: col }))
      return [
        ...(this.isSummaryTemplate
          ? fixed.filter((col, index) => index < 2 || index > 4)
          : fixed),
        ...dynamic
      ]
    },
    tableRecords() {
      return [
        { isLegend: true },
        ...this.resources,
        ...(this.isSummaryTemplate ? this.summaryFooter : [])
      ]
    },
    totals() {
      if (!this.resources || this.resources.length === 0) {
        return {
          po_distance: 0,
          actual_completion: 0,
          progression_percent: 0,
          new_fiber: 0,
          refreshment: 0,
          tasks: {}
        }
      }

      const initial = {
        po_distance: 0,
        actual_completion: 0,
        progression_percent_sum: 0,
        new_fiber: 0,
        refreshment: 0,
        tasks: {}
      }

      this.taskColumns.forEach(col => {
        initial.tasks[col] = 0
      })

      const summary = this.resources.reduce((acc, row) => {
        acc.po_distance += Number(row.po_distance) || 0
        acc.actual_completion += Number(row.actual_completion) || 0
        acc.progression_percent_sum += Number(row.progression_percent) || 0
        acc.new_fiber += Number(row.new_fiber) || 0
        acc.refreshment += Number(row.refreshment) || 0

        this.taskColumns.forEach(col => {
          if (row.tasks && row.tasks[col]) {
            acc.tasks[col] += Number(row.tasks[col]) || 0
          }
        })

        return acc
      }, initial)

      const avgProgression =
        this.resources.length > 0
          ? Math.round(summary.progression_percent_sum / this.resources.length)
          : 0

      return {
        po_distance: summary.po_distance,
        actual_completion: summary.actual_completion,
        progression_percent: avgProgression,
        new_fiber: summary.new_fiber,
        refreshment: summary.refreshment,
        tasks: summary.tasks
      }
    },
    availableTasks() {
      const usedIds = this.orderedTasks.map(t => t.id)
      return this.allTasksRaw
        .filter(t => !usedIds.includes(t.id))
        .map(t => ({
          id: t.id,
          label: t.title
        }))
    },
    syncStatusLabel() {
      if (this.syncing) return this.$t('projectProgressionReport.saving')
      return ''
    },

    // Select All logic for Left Column (Available Tasks)
    selectAllAvailable: {
      get() {
        if (this.availableTasks.length === 0) return false
        return this.checkedTaskIds.length === this.availableTasks.length
      },
      set(val) {
        if (val) {
          this.checkedTaskIds = this.availableTasks.map(t => t.id)
        } else {
          this.checkedTaskIds = []
        }
      }
    },

    // Select All logic for Right Column (Ordered Tasks)
    selectAllOrdered: {
      get() {
        if (this.orderedTasks.length === 0) return false
        return this.checkedOrderedIds.length === this.orderedTasks.length
      },
      set(val) {
        if (val) {
          this.checkedOrderedIds = this.orderedTasks.map(t => t.id)
        } else {
          this.checkedOrderedIds = []
        }
      }
    }
  },
  methods: {
    ...mapActions('report/project', [
      'getProject',
      'getTaskWithMilestone',
      'projectProgressionReport',
      'projectProgressionReportExcel',
      'getProjectWithHierarchy'
    ]),
    ...mapActions('report/objectOrder', ['getObjectOrder', 'storeObjectOrder']),

    async fetchData() {
      this.loading = true
      try {
        const projects = await this.getProjectWithHierarchy().catch(() => [])

        this.projects = Array.isArray(projects)
          ? projects.map(el => ({
              value: el.project_id,
              label: el.project_name
            }))
          : []
      } catch (error) {
        console.error('Error fetching data:', error)
        this.$toasted.error('Failed to load data')
        this.projects = []
      } finally {
        this.loading = false
      }
    },

    async fetchProjectRelatedData() {
      const projectId = this.model.project_id
      this.resources = []
      this.taskColumns = []
      this.orderedTasks = []
      this.checkedTaskIds = []
      this.checkedOrderedIds = []

      if (!projectId) {
        this.allTasksRaw = []
        return
      }

      this.loading = true
      try {
        const tasks = await this.getTaskWithMilestone({
          params: { project_id: projectId }
        }).catch(() => [])

        this.allTasksRaw = Array.isArray(tasks) ? tasks : []

        await this.loadExistingOrder(projectId)
      } catch (error) {
        console.error('Error fetching project tasks:', error)
        this.$toasted.error('Failed to load tasks')
        this.allTasksRaw = []
      } finally {
        this.loading = false
      }
    },

    async loadExistingOrder(projectId) {
      try {
        // Pass payload directly to fix validation error
        const response = await this.getObjectOrder({
          object: REPORT_OBJECT_NAME,
          project_id: projectId
        }).catch(() => null)

        const responseData =
          response && response.data ? response.data : response
        const existing = Array.isArray(responseData)
          ? responseData[0]
          : responseData

        if (existing && existing.id) {
          let savedOrder = existing.order || []
          if (typeof savedOrder === 'string') {
            try {
              savedOrder = JSON.parse(savedOrder)
            } catch (e) {
              savedOrder = []
            }
          }

          this.orderedTasks = savedOrder
            .map(item => {
              const taskId = item.task_id || item.id
              const task = this.allTasksRaw.find(t => t.id === taskId)
              if (!task) return null
              return {
                id: taskId,
                label: task.title,
                order: item.order
              }
            })
            .filter(Boolean)
            .sort((a, b) => a.order - b.order)
        } else {
          this.orderedTasks = []
        }
      } catch (error) {
        console.error('Error loading existing order:', error)
        this.orderedTasks = []
      }
    },

    moveCheckedTasks() {
      if (this.checkedTaskIds.length === 0 || this.syncing) return

      const toMove = this.availableTasks.filter(t =>
        this.checkedTaskIds.includes(t.id)
      )

      toMove.forEach(task => {
        this.orderedTasks.push({
          id: task.id,
          label: task.label,
          order: this.orderedTasks.length + 1
        })
      })

      this.checkedTaskIds = []
      this.syncOrder()
    },

    removeCheckedTasks() {
      if (this.checkedOrderedIds.length === 0 || this.syncing) return

      this.orderedTasks = this.orderedTasks.filter(
        t => !this.checkedOrderedIds.includes(t.id)
      )
      this.reindexOrder()

      this.checkedOrderedIds = []
      this.syncOrder()
    },

    reindexOrder() {
      this.orderedTasks.forEach((t, i) => (t.order = i + 1))
    },

    onDragStart(index) {
      if (this.syncing) return
      this.dragIndex = index
    },
    onDragEnter(index) {
      if (this.syncing || this.dragIndex === null || this.dragIndex === index)
        return
      const list = this.orderedTasks
      const dragged = list.splice(this.dragIndex, 1)[0]
      list.splice(index, 0, dragged)
      this.dragIndex = index
    },
    onDragEnd() {
      if (this.syncing) return
      this.dragIndex = null
      this.reindexOrder()
      this.syncOrder()
    },

    async syncOrder() {
      if (!this.model.project_id) return

      this.syncing = true
      const payload = {
        object: REPORT_OBJECT_NAME,
        project_id: this.model.project_id,
        order: this.orderedTasks.map(t => ({
          task_id: t.id,
          order: t.order
        }))
      }

      try {
        await this.storeObjectOrder(payload)
      } catch (err) {
        this.$toasted.error(err.message || 'Failed to save task order')
      } finally {
        this.syncing = false
      }
    },

    filterOption(input, option) {
      return option.componentOptions.children[0].text
        .toLowerCase()
        .includes(input.toLowerCase())
    },

    onPreview() {
      this.loading = true
      this.projectProgressionReport({ project_id: this.model.project_id })
        .then(result => {
          this.resources = result.rows || []
          this.taskColumns = result.columns || []
        })
        .catch(err => {
          this.$toasted.error(err.message || 'Failed to load report')
        })
        .finally(() => (this.loading = false))
    },

    async onExportExcel() {
      if (!this.model.project_id) {
        this.$toasted.error('Please select a project.')
        return
      }

      this.loadingExport = true

      try {
        const response = await this.projectProgressionReportExcel({
          project_id: this.model.project_id,
          template: this.templateStyle
        })

        let filename = 'project_progression_report.xlsx'
        const disposition =
          response && response.headers
            ? response.headers['content-disposition']
            : null

        if (disposition && disposition.indexOf('filename') !== -1) {
          filename = trim(
            disposition
              .substring(disposition.indexOf('filename'))
              .replace('filename=', ''),
            '"'
          )
        }

        FileDownload(response.data || response, filename)
      } catch (err) {
        let errorMessage = 'Failed to download excel report.'

        if (err instanceof ArrayBuffer) {
          try {
            const parsed = JSON.parse(
              String.fromCharCode.apply(null, new Uint8Array(err))
            )
            errorMessage = parsed.message || errorMessage
          } catch (e) {
            // keep default
          }
        } else if (err && err.message) {
          errorMessage = err.message
        }

        this.$toasted.error(errorMessage)
      } finally {
        this.loadingExport = false
      }
    }
  }
}
</script>

<style scoped>
.progression-ts-table-wrapper {
  overflow-x: auto;
}
.progression-ts-table-wrapper ::v-deep .summary-cell {
  background-color: #dce6f1;
  border: none;
}
.select-all-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #1890ff;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
}
.select-all-label:hover {
  color: #40a9ff;
}
.task-box {
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  min-height: 260px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px;
}
.task-box-empty {
  padding: 12px;
  color: #999;
  font-size: 13px;
  text-align: center;
}
.task-box-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  user-select: none;
}
.task-box-item:hover:not(.is-disabled) {
  background: #f5f9ff;
}
.task-box-item:last-child {
  border-bottom: none;
}
.task-box-item.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}
.task-checkbox {
  flex-shrink: 0;
  cursor: pointer;
}
.arrow-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.arrow-btn {
  width: 44px;
  height: 36px;
  border-radius: 6px;
  border: none;
  outline: none;
  background: #1890ff;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.arrow-btn:focus {
  outline: none;
  box-shadow: none;
}
.arrow-btn:hover:not(:disabled) {
  background: #096dd9;
}
.arrow-btn:disabled {
  background: #d9d9d9;
  cursor: not-allowed;
}
.ordered-item {
  cursor: grab;
}
.ordered-item.is-dragging {
  opacity: 0.4;
  background: #eef4ff;
}
.drag-handle {
  color: #bbb;
  cursor: grab;
}
.order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #1890ff;
  color: #fff;
  font-size: 12px;
  flex-shrink: 0;
}
.task-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Table Styles matching reference images */
.progression-ts-table-wrapper ::v-deep table {
  width: 100%;
  border-collapse: collapse;
}

.progression-ts-table-wrapper ::v-deep thead th {
  background-color: #8bc34a !important;
  color: #1a1a1a !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 13px;
  padding: 10px;
  border: 1px solid #7cb342 !important;
  white-space: nowrap;
}

.progression-ts-table-wrapper ::v-deep tbody tr:nth-child(1) {
  background-color: #e8eef5 !important;
}

.progression-ts-table-wrapper ::v-deep .legend-cell {
  font-weight: 700 !important;
  color: #1b365d !important;
  padding: 10px;
  border: 1px solid #c5d3e8 !important;
}

.progression-ts-table-wrapper ::v-deep tbody td {
  padding: 10px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
}
</style>
